import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const benefits = [
  { icon: "ri-team-line", key: "reliable" },
  { icon: "ri-leaf-line", key: "eco" },
  { icon: "ri-calendar-check-line", key: "flexible" },
  { icon: "ri-price-tag-3-line", key: "affordable" },
  { icon: "ri-thumb-up-line", key: "guaranteed" },
  { icon: "ri-shield-star-line", key: "insured" },
];

const BenefitsSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll(".benefit-item");
            items.forEach((item, i) => {
              setTimeout(() => {
                item.classList.add("opacity-100", "translate-x-0");
                item.classList.remove("opacity-0", "translate-x-8");
              }, i * 100);
            });
            const left = entry.target.querySelector(".benefit-left");
            left?.classList.add("opacity-100", "translate-x-0");
            left?.classList.remove("opacity-0", "-translate-x-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="benefits" className="py-24 bg-[#0F2A44]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-center">
          {/* Left Content */}
          <div className="benefit-left opacity-0 -translate-x-8 transition-all duration-700 lg:col-span-2">
            <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-4 block">
              {t("benefits_label")}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
              {t("benefits_headline")}
            </h2>
            <p className="text-white/65 text-base leading-relaxed mb-8">
              {t("benefits_text")}
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 border-2 border-[#D4AF37] text-[#D4AF37] font-semibold px-7 py-3.5 rounded-full hover:bg-[#D4AF37] hover:text-[#0F2A44] transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t("benefits_cta")}
              <i className="ri-arrow-right-line" />
            </a>

            {/* Image */}
            <div className="mt-10 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20commercial%20cleaning%20crew%20in%20dark%20navy%20uniforms%20with%20industrial%20equipment%20working%20in%20a%20spotless%20gleaming%20medical%20clinic%20corridor%2C%20polished%20floors%20and%20sterile%20white%20walls%2C%20premium%20healthcare%20facility%20cleaning%20photography%2C%20Seattle%20Washington%20state&width=600&height=400&seq=benefits_commercial001&orientation=landscape"
                alt="Prime Shine WA professional commercial cleaning team"
                className="w-full h-52 object-cover object-top"
                loading="lazy"
              />
            </div>
          </div>

          {/* Right Grid */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {benefits.map((b) => (
              <div
                key={b.key}
                className="benefit-item opacity-0 translate-x-8 transition-all duration-500 rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 hover:bg-white/5 transition-colors duration-300"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#D4AF37]/15 mb-4">
                  <i className={`${b.icon} text-xl text-[#D4AF37]`} />
                </div>
                <h3 className="text-white font-bold text-base mb-2">
                  {t(`benefit_${b.key}`)}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed">
                  {t(`benefit_${b.key}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
